// Drafts on the server (signed in with a nickname): open or create, autosave, submit for the
// automatic checks and follow the result live.
import { periodSpec, type LoopData, type PeriodDef } from "@eb/shared";
import { api } from "../api";
import { fetchKit } from "../audio/host";
import { onLive } from "../live";
import { loadIntoEditor, useEditor, type ServerDraftMeta } from "./store";

export interface DraftView extends ServerDraftMeta {
  day: number;
  period: PeriodDef;
  loop: LoopData;
  title: string | null;
}

function meta(d: DraftView, sentOn?: boolean): ServerDraftMeta {
  return {
    id: d.id,
    status: d.status,
    reservedUntil: d.reservedUntil,
    loopId: d.loopId,
    wizardShare: d.wizardShare,
    playsAt: d.playsAt,
    check: d.check,
    sentOn,
  };
}

/** Submitted songs can't change: being checked, waiting for review, or approved. */
const locked = (status: string) => status === "approved" || status === "checking" || status === "review";

export async function loadDraftView(d: DraftView, sentOn?: boolean): Promise<void> {
  const { manifest } = await fetchKit(d.period.kit);
  loadIntoEditor(d.id, d.period, periodSpec(d.period, manifest), d.loop, d.title ?? "");
  useEditor.getState().set({ server: meta(d, sentOn), readOnly: locked(d.status) });
}

const inFlight = new Map<string, Promise<DraftView>>();

/** Open the draft to work on: `draftId` if given; else an open draft (for `day` if given); else a
 *  new one. Concurrent calls with the same arguments share one request (no duplicate drafts). */
export function openServerDraft(opts: { day?: number; draftId?: string; fresh?: boolean }): Promise<DraftView> {
  const key = JSON.stringify(opts);
  let p = inFlight.get(key);
  if (!p) {
    p = openServerDraftOnce(opts).finally(() => setTimeout(() => inFlight.delete(key), 1000));
    inFlight.set(key, p);
  }
  return p;
}

async function openServerDraftOnce(opts: { day?: number; draftId?: string; fresh?: boolean }): Promise<DraftView> {
  if (opts.draftId) {
    const r = await api<{ draft: DraftView }>("GET", `/api/drafts/${opts.draftId}`);
    await loadDraftView(r.draft);
    return r.draft;
  }
  if (!opts.fresh) {
    const list = (await api<{ drafts: DraftView[] }>("GET", "/api/drafts")).drafts;
    const open = list.find(
      (d) =>
        (d.status === "draft" || d.status === "rejected" || d.status === "checking") &&
        (opts.day === undefined || d.day === opts.day),
    );
    if (open) {
      await loadDraftView(open);
      return open;
    }
  }
  const r = await api<{ draft: DraftView; sentOn: boolean }>(
    "POST",
    "/api/drafts",
    opts.day !== undefined ? { day: opts.day } : {},
  );
  await loadDraftView(r.draft, r.sentOn);
  return r.draft;
}

export async function saveServerDraft(): Promise<void> {
  const s = useEditor.getState();
  if (!s.server || s.readOnly) return;
  const r = await api<{ draft: DraftView }>("PUT", `/api/drafts/${s.server.id}`, {
    loop: s.hist.present,
    title: s.title || null,
  });
  useEditor.getState().set({ server: { ...meta(r.draft), sentOn: s.server.sentOn } });
}

export async function submitServerDraft(): Promise<void> {
  await saveServerDraft();
  const s = useEditor.getState();
  if (!s.server) return;
  const r = await api<{ draft: DraftView }>("POST", `/api/drafts/${s.server.id}/submit`, {
    ownWork: true,
    licence: true,
  });
  useEditor.getState().set({ server: meta(r.draft), readOnly: true });
  void waitForCheck(r.draft.id);
}

/** Refresh until the check result arrives (live push or polling, whichever is first). */
export async function waitForCheck(id: string): Promise<void> {
  let done = false;
  const refresh = async () => {
    const r = await api<{ draft: DraftView }>("GET", `/api/drafts/${id}`);
    const s = useEditor.getState();
    if (s.server?.id !== id) return true;
    s.set({ server: meta(r.draft), readOnly: locked(r.draft.status) });
    return r.draft.status !== "checking";
  };
  const off = onLive((m) => {
    if (m.type === "check" && m.draftId === id) void refresh().then((d) => (done = done || d));
  });
  for (let i = 0; i < 120 && !done; i++) {
    await new Promise((r) => setTimeout(r, 1000));
    if (!done) done = await refresh().catch(() => false);
  }
  off();
}

/** While a song waits for review: refresh when an admin decides (live push). Returns the unsubscribe. */
export function watchReview(id: string): () => void {
  return onLive((m) => {
    if (m.type !== "check" || m.draftId !== id) return;
    void api<{ draft: DraftView }>("GET", `/api/drafts/${id}`).then((r) => {
      const s = useEditor.getState();
      if (s.server?.id === id) s.set({ server: meta(r.draft), readOnly: locked(r.draft.status) });
    });
  });
}
